/**
 * The sound a notification makes.
 *
 * Synthesised rather than played from a file: two short sine tones through the
 * Web Audio API weigh nothing, need no asset to load or 404, and can be shaped
 * to be quiet. A notification sound is heard hundreds of times a day by
 * somebody who did not choose it, so the bar is that it is brief, soft, and
 * never startling.
 *
 * Browsers refuse to start an AudioContext until the user has interacted with
 * the page. That is not a bug to work around — it is the guarantee that a page
 * cannot make noise at somebody who has not touched it — so this primes itself
 * on the first real gesture and stays silent until then.
 */

const STORAGE_KEY = "astrum.sound";

let context: AudioContext | null = null;
let primed = false;

/** Whether the user wants sound. Defaults to on, and is remembered per device. */
export function soundEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== "off";
  } catch {
    // Private browsing can throw on access rather than return null.
    return true;
  }
}

export function setSoundEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? "on" : "off");
  } catch {
    /* nothing to do — the preference simply will not persist */
  }
}

/**
 * Create the audio context on a real user gesture.
 *
 * Called once from the shell. Without it the first notification of a session
 * would be silent while the context sat suspended, which reads as the sound
 * being broken rather than as the policy it is.
 */
export function primeAudio(): void {
  if (primed) return;
  primed = true;

  const start = () => {
    try {
      const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      context ??= new Ctor();

      /*
       * Safari unlocks a context only when something is actually played during
       * the gesture — creating one and calling resume() is not enough, and the
       * context stays silent with no error to say so. A one-frame silent
       * buffer, started synchronously inside the handler, is what does it.
       */
      const buffer = context.createBuffer(1, 1, context.sampleRate);
      const source = context.createBufferSource();
      source.buffer = buffer;
      source.connect(context.destination);
      source.start(0);

      void context.resume().then(() => {
        if (context?.state === "running") {
          window.removeEventListener("pointerdown", start);
          window.removeEventListener("keydown", start);
          announce();
        }
      });
    } catch {
      /* audio is a nicety; never let it break the page */
      return;
    }

    /*
     * Kept listening until the context is genuinely running.
     *
     * `resume()` is asynchronous and Safari can leave a context "suspended"
     * well after the gesture that should have unlocked it. Detaching on the
     * first click — which `{ once: true }` would do — spends the only gesture
     * we get and leaves audio permanently dead, so the handler stays until it
     * has evidence the unlock worked.
     */
    if (context?.state === "running") {
      window.removeEventListener("pointerdown", start);
      window.removeEventListener("keydown", start);
      announce();
    }
  };

  window.addEventListener("pointerdown", start);
  window.addEventListener("keydown", start);

  /*
   * Safari suspends the context of a backgrounded tab, and does not always
   * restore it when the tab comes back. Nudging it on the way back means the
   * next notification is audible rather than the first one after it.
   */
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && context?.state === "suspended") {
      void context.resume().then(announce);
    }
  });
}

/**
 * Whether audio is actually unlocked.
 *
 * Reported rather than inferred, because every failure here is silent: a
 * context can be created, resumed, and still never make a sound, and the only
 * way to tell the difference from outside is to ask. Used by the mute control
 * to explain itself when a click has not yet unlocked audio.
 */
export function audioState(): "unavailable" | "waiting" | "ready" {
  if (!context) return "unavailable";
  return context.state === "running" ? "ready" : "waiting";
}

/**
 * Told when audio unlocks, because it happens after the render that asked.
 *
 * `resume()` resolves asynchronously and nothing re-renders when it does, so a
 * control reading `audioState()` during render reports the state at render
 * time and then never corrects itself — showing "waiting for this tab to allow
 * audio" indefinitely on a tab where audio is working perfectly well.
 */
const listeners = new Set<() => void>();

export function onAudioStateChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function announce(): void {
  for (const listener of listeners) listener();
}

/** The last time a sound played, so a burst of messages does not machine-gun. */
let lastPlayedAt = 0;
const MIN_GAP_MS = 2000;

/**
 * A short two-note chime.
 *
 * Rate-limited: ten messages arriving together is one event to a person, and
 * ten overlapping chimes is the fastest way to make somebody turn sound off
 * permanently.
 */
export function playChime(): void {
  if (!soundEnabled()) return;
  if (!context) return;

  /*
   * A suspended context is asked to wake rather than treated as broken.
   *
   * `resume()` is asynchronous, so a context primed a moment ago is often
   * still "suspended" when the first notification arrives — and Safari also
   * re-suspends one whose tab has been in the background, which is exactly
   * when a notification sound matters most. Returning here would drop the
   * sound in both cases.
   */
  if (context.state !== "running") {
    void context.resume().then(() => {
      // Only for a real wake-up, and only once: retrying on every failure
      // would queue a chime for every message received while the tab slept.
      if (context?.state === "running" && Date.now() - lastPlayedAt >= MIN_GAP_MS) ring();
    });
    return;
  }

  if (Date.now() - lastPlayedAt < MIN_GAP_MS) return;
  ring();
}

/**
 * The tones themselves, on a context already known to be running.
 *
 * Separated from `playChime` so the wake-up path can reach it without
 * re-running the checks that sent it there.
 */
function ring(): void {
  if (!context) return;
  lastPlayedAt = Date.now();

  try {
    const at = context.currentTime;
    // A rising major sixth: two notes, unobtrusive, and distinct from the
    // error and alarm sounds every other application on the desktop is using.
    for (const [index, frequency] of [660, 880].entries()) {
      const oscillator = context.createOscillator();
      const gain = context.createGain();

      oscillator.type = "sine";
      oscillator.frequency.value = frequency;

      const start = at + index * 0.09;
      const end = start + 0.16;

      /*
       * Ramped, never switched. A gain that jumps to its value clicks audibly
       * — the discontinuity is a broadband transient — so both ends of the
       * envelope are ramps, and the tail is exponential because loudness is
       * perceived logarithmically.
       */
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.06, start + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, end);

      oscillator.connect(gain).connect(context.destination);
      oscillator.start(start);
      oscillator.stop(end + 0.02);
    }
  } catch {
    /* a failed chime must never surface to the user */
  }
}
